import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SignUpPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    name: '',
    email: '',
    phone: '',
  });

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.username || !form.password || !form.name || !form.email || !form.phone) {
      toast({ title: "All fields are required", variant: "destructive" });
      return;
    }

    if (form.password !== form.confirmPassword) {
      toast({ title: "Passwords do not match", variant: "destructive" });
      return;
    }

    if (form.password.length < 8 || !/[A-Z]/.test(form.password) || !/[a-z]/.test(form.password) || !/[0-9]/.test(form.password)) {
      toast({ title: "Password must be at least 8 characters with uppercase, lowercase, and number", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/signup`, {
        username: form.username,
        password: form.password,
        name: form.name,
        email: form.email,
        phone: form.phone,
      });
      toast({ title: "Account created!", description: "You can now log in." });
      navigate('/login');
    } catch (error) {
      toast({ title: "Sign up failed", description: error.response?.data?.detail || "Please try again", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900" data-testid="signup-title">Create Account</h1>
            <p className="text-gray-500 mt-1">Join JIT Cargo Express</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Username *</Label>
              <Input data-testid="signup-username" value={form.username} onChange={(e) => handleChange('username', e.target.value)} placeholder="Choose a username" className="mt-1" />
            </div>
            <div>
              <Label>Full Name *</Label>
              <Input data-testid="signup-name" value={form.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="Your full name" className="mt-1" />
            </div>
            <div>
              <Label>Email Address *</Label>
              <Input data-testid="signup-email" type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="email@example.com" className="mt-1" />
            </div>
            <div>
              <Label>Phone Number *</Label>
              <Input data-testid="signup-phone" value={form.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="09XXXXXXXXX" className="mt-1" />
            </div>
            <div>
              <Label>Password *</Label>
              <Input data-testid="signup-password" type="password" value={form.password} onChange={(e) => handleChange('password', e.target.value)} placeholder="Min 8 chars, uppercase, lowercase, number" className="mt-1" />
            </div>
            <div>
              <Label>Confirm Password *</Label>
              <Input data-testid="signup-confirm-password" type="password" value={form.confirmPassword} onChange={(e) => handleChange('confirmPassword', e.target.value)} placeholder="Re-enter password" className="mt-1" />
            </div>

            <Button type="submit" disabled={loading} data-testid="signup-submit" className="w-full bg-[#D62828] hover:bg-[#B01F1F] text-white font-bold py-3">
              {loading ? 'Creating Account...' : 'Sign Up'}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#D62828] font-semibold hover:underline" data-testid="goto-login">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
